import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { IsNewDirective } from '../../../../directives/is-new.directive';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { SortComponent } from '../../../core/components/sort/sort.component';
import { StatusComponent } from '../../../core/components/status/status.component';
import { DatepickerComponent } from '../../../core/components/datepicker/datepicker.component';
import { DateRangeInput } from '../../../core/models/date-range-input';
import { processDateRange } from '../../../../helper/date-utils';
import { ToastrService } from 'ngx-toastr';
import { Client, PageInfo, QueryTokenDeviceDetailsDataIn, QueryTokenDeviceDetailsDataOut, SortingType, TokenStatus, TokenType } from '../../../../api/api-reference';
import { SearchComponent } from '../../../core/components/search/search.component';
import { ClipboardService } from '../../../../services/clipboard.service';

@Component({
  selector: 'app-tokens',
  standalone: true,
  imports: [CommonModule, TranslateModule, IsNewDirective, PaginationComponent, SortComponent, StatusComponent, DatepickerComponent, SearchComponent],
  templateUrl: './tokens.component.html',
  styleUrl: './tokens.component.scss',
})
export class TokensComponent implements OnInit {
  @Input() meterId = '';

  tokens: QueryTokenDeviceDetailsDataOut[] = [];
  numberOfTokens = 0;
  lastPageSize = 1;

  pageSize = 10;
  currentPage = 1;

  tokenStatuses: TokenStatus[] = Object.values(TokenStatus);
  tokenTypes: TokenType[] = Object.values(TokenType);

  requestData = new QueryTokenDeviceDetailsDataIn({
    pageInfo: new PageInfo({ pageSize: 10, page: 1 }),
    meterId: undefined,
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
    status: undefined,
    type: undefined,
  });

  isDatepickerOpen = false;
  startDate?: Date;
  endDate?: Date;
  selectedDate: string | null = '';

  constructor(private client: Client, private toastr: ToastrService, public clipboardService: ClipboardService) {}

  ngOnInit(): void {
    this.loadTokens(1);
  }

  loadTokens(page: number = 1): void {
    this.currentPage = page;
    this.requestData.pageInfo!.page = page;
    this.requestData.pageInfo!.pageSize = this.pageSize;
    this.requestData.meterId = this.meterId || undefined;
    this.client.queryTokensDeviceDetails(this.requestData).subscribe({
      next: (response) => {
        this.tokens = response.data ?? [];
        this.numberOfTokens = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfTokens / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(page: number) {
    this.loadTokens(page);
  }

  onSortOption(option: string) {
    this.requestData.sorting = option === 'ASCENDING' ? SortingType.Ascending : SortingType.Descending;
    this.loadTokens(1);
  }

  onStatusOption(status: string) {
    this.requestData.status = status === 'ALL' ? undefined : (status as TokenStatus);
    this.loadTokens(1);
  }

  onTypeOption(type: string) {
    this.requestData.type = type === 'ALL' ? undefined : (type as TokenType);
    this.loadTokens(1);
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText || undefined;
    this.loadTokens(1);
  }

  toggleDatepicker() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);
    this.startDate = result.startDate;
    this.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.loadTokens(1);
  }

  clearDates(): void {
    this.startDate = undefined;
    this.endDate = undefined;
    this.selectedDate = '';
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.loadTokens(1);
  }

  printResponseOfCommand(event: MouseEvent, row: QueryTokenDeviceDetailsDataOut) {
    if (row.tokenStatus !== TokenStatus.Failed) return;
    event.stopPropagation();
    if (row.errorMessage) this.toastr.error(row.errorMessage);
  }
}
